import React, { useCallback, useEffect, useState } from 'react';
import { subscriptionService } from '../services/api';

/**
 * Shown when the tenant's subscription is past due and the current user is a
 * tenant_admin in read-only mode (the backend guard refuses writes with a 402
 * access:"read_only"). The api interceptor sets a flag + dispatches an event.
 *
 * The flag is only ever SET by that 402 handler, so it goes stale the moment the
 * tenant actually pays (marking the invoice paid restores the subscription to
 * active server-side). It is therefore cleared three ways:
 *   1. a successful write — the api interceptor dispatches -cleared,
 *   2. a server re-check on mount and on tab focus (below),
 *   3. logout (localStorage wiped).
 */
const READONLY_STATES = ['past_due', 'suspended'];
const DEFAULT_MSG = 'Read-only mode — your subscription is past due. Renew to restore full access.';

export default function ReadOnlyBanner() {
  const [msg, setMsg] = useState<string | null>(() =>
    localStorage.getItem('subscription_readonly') ? DEFAULT_MSG : null,
  );

  const clear = useCallback(() => {
    localStorage.removeItem('subscription_readonly');
    setMsg(null);
  }, []);

  // Ask the server whether the tenant is still restricted. Cheap: only runs
  // while the banner is actually showing.
  const verify = useCallback(() => {
    if (!localStorage.getItem('subscription_readonly')) return;
    subscriptionService.getSubscription()
      .then((r) => {
        const state = (r.data as any)?.state;
        if (state && !READONLY_STATES.includes(state)) clear();
      })
      .catch(() => { /* no tenant context / offline — leave the banner as-is */ });
  }, [clear]);

  useEffect(() => {
    verify();
    const onSet = (e: Event) => setMsg((e as CustomEvent).detail || DEFAULT_MSG);
    const onCleared = () => setMsg(null);
    window.addEventListener('subscription-readonly', onSet);
    window.addEventListener('subscription-readonly-cleared', onCleared);
    window.addEventListener('focus', verify);
    return () => {
      window.removeEventListener('subscription-readonly', onSet);
      window.removeEventListener('subscription-readonly-cleared', onCleared);
      window.removeEventListener('focus', verify);
    };
  }, [verify]);

  if (!msg) return null;
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 9999, background: '#f59e0b', color: '#3b2f00',
      padding: '8px 16px', textAlign: 'center', fontSize: 14, fontWeight: 600,
    }}>
      ⚠ {msg}
    </div>
  );
}
